import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/lab-pack-services-page.module.css'
// eslint-disable-next-line import/extensions
import labPack from '../../json/lab-pack-services-data.json'

const labPackServiceData = {
  name: 'Lab Pack Services',
  description:
    'Chemist-led lab pack services for businesses, schools, and laboratories. Dimension identifies, segregates, and over-packs small-container laboratory chemicals — including unknowns — into DOT-approved drums, with full EPA, DOT, and RCRA documentation.',
  serviceType: 'Lab Pack and Laboratory Chemical Disposal',
  url: 'https://getdimension.com/lab-pack-services',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Laboratory Chemical Pack-Out' },
    { name: 'Lab Decommissioning and Clean-Out' },
    { name: 'Unknown and Unlabeled Chemical Identification' },
    { name: 'Hazard Categorization (HazCat)' },
    { name: 'DOT-Approved Over-Packing and Manifesting' },
  ],
}

const labPackFAQData = {
  questions: labPack.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function LabPackServices() {
  return (
    <>
      <Head>
        <title>Lab Pack Services — Laboratory Chemical Disposal | Dimension</title>
        <meta
          name="description"
          content="Chemist-led lab pack services for labs, schools & businesses — identification, segregation, and DOT-approved pack-out of small-container chemicals, including unknowns. Instant quote, fully documented."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/lab-pack-services" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={labPackServiceData} />
        <SchemaMarkup type="FAQPage" data={labPackFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heading={labPack.pitch.heading}
          material={labPack.pitch.material}
          pitchText={labPack.pitch.pitchText}
          ctaLink={labPack.pitch.ctaLink}
          ctaText={labPack.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...labPack.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...labPack.benefits} />
        <CaseStudy {...labPack.caseStudy} />
        <Approach {...labPack.approach} />
        <FAQ {...labPack.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
            },
            {
              label: 'Corrosive Waste Disposal',
              href: '/corrosive-waste-disposal',
              description:
                'Compliant disposal of waste acids, caustics, and other DOT Class 8 corrosive liquids.',
            },
            {
              label: 'Flammable Waste Disposal',
              href: '/flammable-waste-disposal',
              description:
                'Compliant disposal of solvents, used fuel, aerosols, and other DOT Class 3 ignitable waste.',
            },
            {
              label: 'Regulated Waste Disposal',
              href: '/regulated-waste-disposal',
              description:
                'The catch-all for EPA- and DOT-regulated materials — universal waste, batteries, aerosols, gases, and more.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default LabPackServices
